import React from 'react';
import { Grid, Typography, Paper, makeStyles } from '@material-ui/core';
import ActivityIndicator from '../../ActivityIndicator';
import { convertMinutesToHours } from '../../../utils/formatTime';
import { PdvDeliveryTime } from '../../../hooks/freights/useGetFreightStatistics';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  title: {
    fontFamily: 'Quicksand, sans-serif',
    fontSize: '1.7rem',
    color: '#1c2434',
    fontWeight: 600,
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '0.2rem',
  },
  paragraph: {
    fontFamily: 'Quicksand, sans-serif',
    fontWeight: 500,
    color: '#1c2434',
    fontSize: '1rem',
    lineHeight: '1rem',
  },
  text: {
    fontFamily: 'Quicksand, sans-serif',
    fontWeight: 500,
    fontSize: '1rem',
    lineHeight: '1rem',
    color: '#1c2434',
    textAlign: 'left',

    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface Props {
  data: PdvDeliveryTime[];
  loading: boolean;
}

const TableStatistics: React.FC<Props> = ({ data, loading }) => {
  const [sortBy, setSortBy] = React.useState<'min' | 'max' | 'avg' | 'name'>(
    'name',
  );
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>(
    'asc',
  );

  const classes = useStyles();

  const handleSortBy = (sortField: 'min' | 'max' | 'avg' | 'name') => {
    if (sortBy === sortField) {
      setSortDirection(prevDirection =>
        prevDirection === 'asc' ? 'desc' : 'asc',
      );
    } else {
      setSortBy(sortField);
      setSortDirection('asc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'min':
        comparison = (a.minDeliveryTime || 0) - (b.minDeliveryTime || 0);
        break;
      case 'max':
        comparison = (a.maxDeliveryTime || 0) - (b.maxDeliveryTime || 0);
        break;
      case 'avg':
        comparison = (a.avgDeliveryTime || 0) - (b.avgDeliveryTime || 0);
        break;
      case 'name':
      default:
        comparison = a.corporateName.localeCompare(b.corporateName);
        break;
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  if (loading) {
    return <ActivityIndicator />;
  }

  return (
    <Paper className={classes.root}>
      <Typography className={classes.title}>
        Estatística de tempo de entrega
      </Typography>
      <hr />
      <Grid container spacing={1} className={classes.container}>
        <Grid
          item
          xs={12}
          sm={6}
          md={3}
          className={classes.text}
          style={{ color: '#64748B' }}
          onClick={() => handleSortBy('name')}
        >
          PDV's
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={3}
          className={classes.paragraph}
          style={{ color: '#64748B' }}
          onClick={() => handleSortBy('min')}
        >
          Menor tempo
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={3}
          className={classes.paragraph}
          style={{ color: '#64748B' }}
          onClick={() => handleSortBy('max')}
        >
          Maior tempo
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={3}
          className={classes.paragraph}
          style={{ color: '#64748B' }}
          onClick={() => handleSortBy('avg')}
        >
          Tempo Médio
        </Grid>
      </Grid>
      <hr />

      {sortedData.map((pdv, index) => (
        <React.Fragment key={index}>
          <Grid container spacing={1} className={classes.container} key={index}>
            <Grid item xs={12} sm={6} md={3} className={classes.text}>
              {pdv?.corporateName}
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.paragraph}>
              {pdv?.minDeliveryTime
                ? convertMinutesToHours(Number(pdv.minDeliveryTime).toFixed(2))
                : 'Sem dados'}
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.paragraph}>
              {pdv?.maxDeliveryTime
                ? convertMinutesToHours(Number(pdv.maxDeliveryTime).toFixed(2))
                : 'Sem dados'}
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              className={classes.paragraph}
              style={{ color: '#10B981' }}
            >
              {pdv?.avgDeliveryTime
                ? convertMinutesToHours(Number(pdv.avgDeliveryTime).toFixed(2))
                : 'Sem dados'}
            </Grid>
          </Grid>
          <hr />
        </React.Fragment>
      ))}
    </Paper>
  );
};

export default TableStatistics;
